import { Stack, SystemProps, Text } from "flicket-ui";
import { pick } from "@styled-system/props";
import { useEffect, useState } from "react";

const timeDivisionLabels = ["Days", "Hrs", "Mins", "Secs"] as const;
type TimeDivisionLabel = (typeof timeDivisionLabels)[number];

interface CountdownTimerProps extends SystemProps {
  countdownTo: Date;
  onComplete?: () => void;
}

const CountdownNumberCard = ({
  value,
  timeDivision,
}: {
  value: number;
  timeDivision: TimeDivisionLabel;
}) => {
  return (
    <Stack
      direction={"vertical"}
      width={"64px"}
      height={"72px"}
      justifyContent={"center"}
      alignItems={"center"}
      borderRadius={"xs"}
      backgroundColor={"white"}
      boxShadow={"container"}
      borderStyle={"none"}
      p={2}
      gap="1/2"
      flexShrink={0}
    >
      <Text variant="header.L" fontSize={"28px" as any} lineHeight="normal">
        {value}
      </Text>
      <Text variant="small" fontSize={"11px" as any} lineHeight="normal">
        {timeDivision}
      </Text>
    </Stack>
  );
};

export const CountdownTimer = ({
  countdownTo,
  onComplete,
  ...props
}: CountdownTimerProps) => {
  const targetTimestamp = countdownTo.getTime();
  const calculateTimeLeft = () => {
    const totalSeconds = Math.max(
      Math.floor((targetTimestamp - Date.now()) / 1000),
      0
    );

    const days = Math.floor(totalSeconds / (60 * 60 * 24));
    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds, totalSeconds };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    // Use Date.now in calculateTimeLeft as it's more
    // accurate than using setInterval.
    const interval = setInterval(() => {
      const next = calculateTimeLeft();
      setTimeLeft((prev) => {
        if (next.totalSeconds === 0 && prev.totalSeconds !== 0) {
          clearInterval(interval); // Stop ticking when done
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTimestamp]);

  useEffect(() => {
    if (timeLeft.totalSeconds === 0) {
      onComplete?.();
    }
  }, [timeLeft.totalSeconds, onComplete]);

  const { days, hours, minutes, seconds } = timeLeft;

  return (
    <Stack gap={"1/2"} width={"fit-content"} flexWrap="nowrap" {...pick(props)}>
      <CountdownNumberCard timeDivision="Days" value={days} />
      <CountdownNumberCard timeDivision="Hrs" value={hours} />
      <CountdownNumberCard timeDivision="Mins" value={minutes} />
      <CountdownNumberCard timeDivision="Secs" value={seconds} />
    </Stack>
  );
};
